<template>
  <v-row no-gutters class="fill-height">
    <v-col cols="12">
      <card-title-row class="pl-0 pt-5" translation-path="sidebar.sewerDataUpstream.title" :has-buttons="false">
        <template #subtitle>
          <italic-subheader
            translation-path="sidebar.sewerDataUpstream.subtitle"
            style="font-size: 12px !important;"
            class="text-left"
          />
        </template>
      </card-title-row>
      <sidebar-row class="pt-5">
        <v-col cols="8" class="px-0 pb-3">
          <v-tooltip bottom max-width="300">
            <template #activator="{ on }">
              <span v-on="on">
                <data-input v-model="currentPipesLayer" v-bind="currentPipesInput" />
              </span>
            </template>
            {{ getLayerTooltip(currentPipesLayer ? currentPipesLayer.name : '') }}
          </v-tooltip>
        </v-col>
      </sidebar-row>
      <sidebar-row>
        <v-col cols="8" class="px-0 pb-3">
          <v-tooltip bottom max-width="300">
            <template #activator="{ on }">
              <span v-on="on">
                <data-input v-model="currentManholesLayer" v-bind="currentManholesInput" />
              </span>
            </template>
            {{ getLayerTooltip(currentManholesLayer ? currentManholesLayer.name : '') }}
          </v-tooltip>
        </v-col>
      </sidebar-row>
      <sidebar-row>
        <v-col class="text-left px-0 pb-0">
          <v-tooltip bottom :disabled="sewerPipesAndManholesAdded && !isManholeSelected" max-width="300">
            <template #activator="{ on }">
              <span v-on="on">
                <base-button-subheader subheader-translation-path="sidebar.sewerDataUpstream.selectManholeSubtitle">
                  <base-button
                    @click="toggleSelecting"
                    class="mr-0"
                    translation-path="sidebar.sewerDataUpstream.selectManhole"
                    :button-style="buttonStyle"
                    :disabled="!sewerPipesAndManholesAdded || (isManholeSelected && !isButtonActive)"
                  />
                </base-button-subheader>
              </span>
            </template>
            {{ buttonTooltip }}
          </v-tooltip>
        </v-col>
      </sidebar-row>
      <sidebar-row>
        <v-col cols="12" class="pa-0">
          <v-checkbox class="ma-0 py-5" v-model="isStopAnalysis" v-bind="stopAnalysisInput" />
        </v-col>
      </sidebar-row>
      <template v-if="isManholeSelected">
        <sidebar-row class="pr-5">
          <v-list class="py-0">
            <template v-for="feature in computedSelectedManholes">
              <v-list-item :key="feature" class="hover-list-element">
                <v-list-item-content class="pa-0">
                  <v-list-item-title>
                    <v-subheader class="pl-0 font--text text-body-2" style="height: auto;">
                      ID: {{ feature }}
                    </v-subheader>
                  </v-list-item-title>
                </v-list-item-content>
                <v-list-item-action>
                  <v-icon @click="deleteFeature(feature)" color="rgba(0,0,0,.7)" size="19px" :disabled="isLoading">
                    mdi-close
                  </v-icon>
                </v-list-item-action>
              </v-list-item>
            </template>
          </v-list>
        </sidebar-row>
        <sidebar-row class="pt-5" slot-cols="auto">
          <base-button
            class="mr-0"
            translation-path="sidebar.sewerDataUpstream.runAnalysis"
            @click="runAnalysis"
            :loading="isLoading"
          />
        </sidebar-row>
      </template>
    </v-col>
  </v-row>
</template>
<script>
import { call, get } from 'vuex-pathify';
import CardTitleRow from '@/components/CardTitleRow';
import DataInput from '@/components/DataInput';
import ItalicSubheader from '@/components/ItalicSubheader';
import SidebarRow from '@/components/SidebarRow';
export default {
  name: 'SewerDataUpstream',
  components: { CardTitleRow, DataInput, ItalicSubheader, SidebarRow },
  computed: {
    activeTool: get('tools/activeTool'),
    projectElements: get('layers/project@layers'),
    computedSelectedManholes() {
      return this.selectedManholes || [];
    },
    selectedManholes: get('map/selectedFeatures@:currentManholesLayerId'),
    selectedManhole() {
      return this.computedSelectedManholes[0] || undefined;
    },
    sewerPipesDataSource: get('admin/modulesMapping@sewer.sewer_pipes_data_source'),
    sewerManholesDataSource: get('admin/modulesMapping@sewer.sewer_manholes_data_source'),
    buttonStyle() {
      return this.isButtonActive
        ? {
            backgroundColor: '#E8F0FE !important',
            border: '1px solid rgba(26, 115, 232, 0.3) !important',
            color: this.$_colors.primary,
          }
        : {
            backgroundColor: 'rgba(0, 0, 0, 0.04) !important',
            border: '1px solid rgba(0, 0, 0, 0.2) !important',
            color: '#61646D',
          };
    },
    buttonTooltip() {
      return this.sewerPipesAndManholesAdded
        ? this.$i18n.t('sidebar.sewerDataUpstream.onlyOneManhole')
        : this.$i18n.t(`snackbar.modulesNotConfigurate.${this.notConfiguratedPath}`);
    },
    currentManholesInput() {
      return {
        dataType: { name: 'select' },
        disabled: !this.sewerPipesAndManholesAdded || this.isLoading,
        hideDetails: true,
        items: this.sewerManholesLayers,
        itemText: 'name',
        label: this.$i18n.t('sidebar.sewerDataUpstream.sewerManholesLayer'),
        returnObject: true,
      };
    },
    stopAnalysisInput() {
      return {
        disabled: !this.sewerPipesAndManholesAdded || this.isLoading,
        label: this.$i18n.t('sidebar.sewerDataUpstream.stopAnalysis'),
        hideDetails: true,
      };
    },
    currentManholesLayerId() {
      return this.currentManholesLayer?.id;
    },
    currentPipesInput() {
      return {
        dataType: { name: 'select' },
        disabled: !this.sewerPipesAndManholesAdded || this.isLoading,
        hideDetails: true,
        items: this.sewerPipesLayers,
        itemText: 'name',
        label: this.$i18n.t('sidebar.sewerDataUpstream.sewerPipesLayer'),
        returnObject: true,
      };
    },
    currentPipesLayerId() {
      return this.currentPipesLayer?.id;
    },
    isManholeSelected() {
      return this.selectedManhole ? true : false;
    },
    notConfiguratedPath() {
      if (this.sewerPipesDataSourceAdded) {
        return 'sewerDataUpstreamManholes';
      } else if (this.sewerManholesDataSourceAdded) {
        return 'sewerDataUpstreamPipes';
      } else {
        return 'sewerDataUpstreamPipesAndManholes';
      }
    },
    projectLayers() {
      return this.$_getFlatGroupsLayers(this.projectElements).layers;
    },
    sewerManholesLayers() {
      this.$forceUpdate();
      return this.projectLayers.filter(layer => layer.data_source_name === this.sewerManholesDataSource);
    },
    sewerManholesDataSourceAdded() {
      return this.sewerManholesLayers?.length > 0;
    },
    sewerPipesLayers() {
      this.$forceUpdate();
      return this.projectLayers.filter(layer => layer.data_source_name === this.sewerPipesDataSource);
    },
    sewerPipesDataSourceAdded() {
      return this.sewerPipesLayers?.length > 0;
    },
    sewerPipesAndManholesAdded() {
      return this.sewerManholesDataSourceAdded && this.sewerPipesDataSourceAdded;
    },
  },
  data: () => ({
    analysisResult: [],
    currentManholesLayer: undefined,
    currentPipesLayer: undefined,
    isStopAnalysis: false,
    isButtonActive: false,
    isLoading: false,
  }),
  methods: {
    getUpstreamNodes: call('modeling/getUpstreamNodes'),
    clearSelection(layerId) {
      this.turnOffSelecting();
      if (layerId || layerId === 0) {
        this.deleteFeature(null, layerId);
      } else {
        this.deleteFeature(null);
      }
    },
    deleteFeature(id, layerId = this.currentManholesLayerId) {
      const deletedIds = id ? [id] : [];
      this.$store.set('map/DELETE_SELECTED_FEATURES!', {
        type: 'selectedFeatures',
        features: {
          [layerId]: deletedIds,
        },
      });
    },
    selectFeatures(layerId, results) {
      this.$store.set('map/ADD_SELECTED_FEATURES!', {
        type: 'selectedFeatures',
        features: {
          [layerId]: results,
        },
      });
    },
    getLayerTooltip(name) {
      return this.sewerPipesAndManholesAdded
        ? name
        : this.$i18n.t(`snackbar.modulesNotConfigurate.${this.notConfiguratedPath}`);
    },
    async runAnalysis() {
      try {
        this.isLoading = true;
        const nodes = await this.getUpstreamNodes({
          oid: this.selectedManhole,
          body: {
            manhole_layer: this.currentManholesLayerId,
            pipe_layer: this.currentPipesLayerId,
            stop_at_pumping_station: this.isStopAnalysis,
          },
        });
        if (nodes.data.data.length > 0) {
          this.analysisResult = nodes.data.data;
          if (this.$route.params.lid) {
            this.deleteFeature(this.currentPipesLayerId, null);
            this.selectFeatures(this.currentPipesLayerId, this.analysisResult);
          } else {
            this.$router.push({
              name: 'sewerDataUpstreamLayer',
              params: { lid: this.currentPipesLayerId },
            });
          }
        } else {
          this.$store.set('snackbar/PUSH_MESSAGE!', {
            message: this.$i18n.t('snackbar.pipesForManholeNotFound'),
          });
        }
        this.isLoading = false;
      } catch {
        this.isLoading = false;
      }
    },
    toggleSelecting() {
      this[`turn${this.isButtonActive ? 'Off' : 'On'}Selecting`]();
    },
    turnOffSelecting() {
      if (this.activeTool !== 'selection') {
        return;
      }
      this.$root.$emit('selection-action');
      this.isButtonActive = false;
    },
    turnOnSelecting() {
      if (this.currentManholesLayerId) {
        this.$root.$emit('setProjectLayerStyle', this.currentManholesLayerId, {
          selectedColors: { selectedColor: '#FF0000', haloColorSelected: '#FF0000' },
        });
      }
      this.$root.$emit('attachOnClickSelection', this.currentManholesLayerId);
      this.isButtonActive = true;
    },
  },
  watch: {
    activeTool(nV, oV) {
      if (nV !== oV && oV === 'selection') {
        this.isButtonActive = false;
      }
    },
    currentManholesLayerId(nV, oV) {
      if (this.$route.name === 'sewerDataUpstreamLayer') {
        this.$router.push({ name: 'sewerDataUpstream' });
      }
      if ((oV || oV === 0) && oV !== nV) {
        this.$root.$emit('setProjectLayerStyle', oV);
      }
      this.clearSelection(oV);
    },
    currentPipesLayerId() {
      if (this.$route.name === 'sewerDataUpstreamLayer') {
        this.$router.push({ name: 'sewerDataUpstream' });
      }
    },
    isManholeSelected(nV) {
      if (nV) {
        this.turnOffSelecting();
      } else if (this.$route.name === 'sewerDataUpstreamLayer') {
        this.$router.push({ name: 'sewerDataUpstream' });
      }
    },
    sewerManholesLayers: {
      deep: true,
      immediate: true,
      handler(nV) {
        if (!nV) {
          return;
        }
        if (nV.find(layer => layer.id === this.currentManholesLayerId)) {
          return;
        } else {
          this.currentManholesLayer = nV[0];
        }
      },
    },
    sewerPipesLayers: {
      deep: true,
      immediate: true,
      handler(nV) {
        if (!nV) {
          return;
        }
        if (nV.find(layer => layer.id === this.currentPipesLayerId)) {
          return;
        } else {
          this.currentPipesLayer = nV[0];
        }
      },
    },
  },
  beforeDestroy() {
    this.clearSelection();
    if (this.currentManholesLayerId) {
      this.$root.$emit('setProjectLayerStyle', this.currentManholesLayerId);
    }
    this.$root.$off('tableDataRowsFetched');
  },
  mounted() {
    this.currentPipesLayer = this.sewerPipesLayers[0];
    this.currentManholesLayer = this.sewerManholesLayers[0];
    this.$root.$on('tableDataRowsFetched', () => {
      this.selectFeatures(this.currentPipesLayerId, this.analysisResult);
    });
  },
};
</script>
<style lang="scss" scoped>
.v-list {
  @include border();
  .v-list-item {
    &:not(:last-child) {
      @include border(bottom);
    }
  }
}
::v-deep .flex-wrap {
  flex-wrap: nowrap !important;
}
</style>
